import React, { useState, useEffect, useRef, useMemo } from 'react';
import JessibucaPlayer, {
  rateConfig,
} from '@/components/JessibucaPlaybackPlayer';
import styles from './style.less';
import classnames from 'classnames';
import { useRequest, request, useLocation } from 'umi';
import { useUpdateEffect } from 'ahooks';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { startsWithExcludedString, getPrefix } from '@/util';
import { message } from 'antd';

let apiPrefix = 'https://open.mokan.cn';

/**
 * 处理回放时间列表，过滤跨天数据
 * 如果某条记录的 start 时间是前一天的（比如 23:56），则将 start 调整为当天 00:00:00
 * @param {Array} times - 原始时间列表 [{start, end}, ...]，时间戳为毫秒
 * @param {string} currentDate - 当前选择的日期，格式 'YYYY-MM-DD'
 * @returns {Array} 处理后的时间列表，时间戳为秒
 */
const processPlaybackTimes = (times, currentDate) => {
  if (!times || !times.length || !currentDate) return [];

  // 计算当天 00:00:00 的时间戳（秒）
  const dayStartTimestamp = dayjs(currentDate, 'YYYY-MM-DD')
    .startOf('day')
    .unix();

  return times.map(({ start, end }) => {
    const startSec = Math.floor(parseInt(start) / 1000);
    const endSec = Math.floor(parseInt(end) / 1000);

    // 如果 start 时间早于当天 00:00:00，说明是跨天数据，将 start 调整为当天 00:00:00
    const adjustedStart =
      startSec < dayStartTimestamp ? dayStartTimestamp : startSec;

    return {
      start: adjustedStart,
      end: endSec,
    };
  });
};

const Playback = () => {
  const location = useLocation();
  const {
    type,
    deviceId,
    serialNumber,
    timestamp,
    clientCode,
    hideVoiceBtn,
    sign,
    bindTime,
  } = location.query;

  const playerRef = useRef();
  const [active, setActive] = useState(false);
  const [isMute, setIsMute] = useState(true);
  const [isHD, setIsHD] = useState(false);
  const [url, setUrl] = useState('');
  const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [playbackDates, setPlaybackDates] = useState([]);
  const [playingDate, setPlayingDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [isPause, setIsPause] = useState(false);
  const [hasMore, sethasMore] = useState(false); // 列表是否有更多数据
  const [deviceInfo, setDeviceInfo] = useState(null); //设备信息
  const [loadLock, setloadLock] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true); // 新增状态来跟踪首次加载
  const [currentSpeed, setCurrentSpeed] = useState(1); //当前倍速
  const currentSpeedRef = useRef(1);
  const [speedListShow, setSpeedListShow] = useState(false);
  const [startTime, setStartTime] = useState(null);

  useUpdateEffect(() => {
    currentSpeedRef.current = currentSpeed;
  }, [currentSpeed]);

  const memoizedPlaybackTimes = useMemo(
    () => processPlaybackTimes(playbackDates, date),
    [playbackDates, date],
  );

  const handleSeek = (data, timestamp) => {
    let targetIndex = -1;
    const targetItem = playbackDates.find((item, index) => {
      const startTs = parseInt(item.start);
      const endTs = parseInt(item.end);
      if (timestamp >= startTs && timestamp <= endTs) {
        targetIndex = index;
        return true;
      }
      return false;
    });

    if (targetItem && targetIndex !== -1) {
      setPlayingDate(`${date}-${targetItem}-${targetIndex}`);
      setStartTime(String(timestamp));
      _playback({
        startTime: String(timestamp),
      }).then((response) => {
        setUrl(response.data.hls || response.data.flvs);
        setTimeout(() => {
          if (isMute) {
            playerRef.current.mute();
          } else {
            playerRef.current.cancelMute();
          }
          if (currentSpeedRef.current !== 1 && playerRef.current) {
            playerRef.current.forward(currentSpeedRef.current);
          }
        }, 500);
        setIsPause(true);
      });
    } else {
      message.warning('该时间段没有回放记录');
    }
  };

  useUpdateEffect(() => {
    if (playerRef.current) {
      if (active) {
        playerRef.current.setRotate(270);
      } else {
        playerRef.current.setRotate(0);
      }
      playerRef.current.resize();
    }
  }, [active]);

  if (type === 'test') {
    apiPrefix = 'https://test-open.mokan.cn';
  }

  const tempObj = {};
  if (serialNumber) {
    tempObj.serialNumber = serialNumber;
  } else if (deviceId) {
    tempObj.deviceId = deviceId;
  }

  const _getPlayBackList = (startTime) => {
    if (loadLock) return;
    setloadLock(true);

    // 计算开始时间
    let startUnix;
    if (initialLoad) {
      // 如果是首次加载，且bindTime存在且日期与date相同，则使用bindTime，否则使用date的时间戳
      const dateOnly = dayjs(date, 'YYYY-MM-DD');
      const bindTimeDateOnly = bindTime
        ? dayjs(Number(bindTime)).startOf('day')
        : null;
      if (bindTimeDateOnly && dateOnly.isSame(bindTimeDateOnly)) {
        startUnix = bindTime;
      } else {
        startUnix = dateOnly.unix() * 1000;
      }
      setInitialLoad(false); // 设置完成后更新状态，避免后续再次执行
    } else {
      const dateOnly = dayjs(date, 'YYYY-MM-DD');
      const bindTimeDateOnly = bindTime
        ? dayjs(Number(bindTime)).startOf('day')
        : null;
      if (bindTimeDateOnly && dateOnly.isSame(bindTimeDateOnly)) {
        startUnix = bindTime;
      } else {
        startUnix = dateOnly.unix() * 1000;
      }
    }

    //计算结束时间
    let endTime = null;
    if (bindTime && startUnix == bindTime) {
      // 当 startUnix 等于 bindTime 时，计算第二天的零点
      endTime = dayjs(Number(startUnix)).add(1, 'day').startOf('day').valueOf(); // 获取第二天的零点时间戳
    } else {
      endTime = startUnix + 24 * 60 * 60 * 1000;
    }

    request(`${apiPrefix}/openapi/video/getPlayBackList`, {
      method: 'post',
      headers: {
        timestamp,
        clientCode,
        sign,
      },
      params: {
        ...tempObj,
        startTime: startTime ? startTime : String(startUnix),
        endTime: String(endTime), // 使用计算的 endTime,
      },
    })
      .then((response) => {
        setloadLock(false);
        setPlaybackDates(
          startTime
            ? [...playbackDates, ...response.data.times]
            : response.data.times,
        );
        setLoading(false);
        sethasMore(response.data.times.length >= 63);
      })
      .catch(() => {
        setLoading(false);
        setloadLock(false);
      });
  };

  const _playback = ({ startTime }) => {
    return request(`${apiPrefix}/openapi/video/startPlayBack`, {
      method: 'post',
      headers: {
        timestamp,
        clientCode,
        sign,
      },
      params: {
        ...tempObj,
        startTime,
        force: true,
        mediaType: 'hls',
        voice: true,
      },
    });
  };

  useEffect(() => {
    if (serialNumber || deviceId) {
      getDeviceInfo().then((res) => {
        setDeviceInfo(res.data);
      });
    }
  }, []);
  function getDeviceInfo() {
    return request(`${apiPrefix}/openapi/ai/getDeviceInfo`, {
      method: 'get',
      headers: {
        timestamp,
        clientCode,
        sign,
      },
      params: tempObj,
    });
  }
  // 切换日期
  useEffect(() => {
    setPlaybackDates([]);
    sethasMore(false);
    setLoading(true);
    _getPlayBackList();
  }, [date]);
  // 杰峰设备回放超过63段时，表示还有下一页，需滚动加载
  const handleScroll = (e) => {
    if (
      Math.ceil(e.currentTarget.scrollTop + e.currentTarget.clientHeight) >=
        e.currentTarget.scrollHeight - 20 &&
      hasMore
    ) {
      _getPlayBackList(playbackDates[playbackDates.length - 1].end);
    }
  };
  const minDate = bindTime
    ? dayjs(Number(bindTime)).format('YYYY-MM-DD')
    : null;
  const handleLeftClick = () => {
    if (minDate && date === minDate) {
      message.error('没有更多数据了');
    } else {
      setDate(
        dayjs(date, 'YYYY-MM-DD').subtract(1, 'days').format('YYYY-MM-DD'),
      );
    }
  };
  return (
    <div className={styles.container}>
      <div
        onClick={() => {
          setActive(false);
        }}
        className={classnames('exit', {
          active,
        })}
      >
        退出全屏
      </div>
      <div
        className={classnames('player', {
          active,
        })}
      >
        <JessibucaPlayer
          playUrl={url}
          ref={playerRef}
          playbackTimes={memoizedPlaybackTimes}
          currentDate={date}
          onSeek={handleSeek}
          startTime={startTime}
        />
      </div>
      <div
        className={classnames('bar', {
          active,
        })}
      >
        {!hideVoiceBtn && (
          <div className="item">
            {isMute ? (
              <img
                onClick={() => {
                  playerRef.current.cancelMute();
                  setIsMute(false);
                }}
                className="mute"
                src={require('./assets/mute.png')}
                alt=""
              />
            ) : (
              <img
                onClick={() => {
                  playerRef.current.mute();
                  setIsMute(true);
                }}
                className="mute"
                src={require('./assets/voice.png')}
                alt=""
              />
            )}
          </div>
        )}
        {getPrefix(deviceInfo?.deviceType) === 'JF_' && url ? (
          <div className="speedContainer">
            <div>
              <span
                className="currentSpeed"
                onClick={() => {
                  setSpeedListShow(speedListShow ? false : true);
                }}
              >
                {currentSpeed === 1 ? '倍速' : currentSpeed + 'X'}
              </span>
            </div>
            {speedListShow ? (
              <div className="speedList">
                <ul>
                  {rateConfig.map((item, index) => {
                    return (
                      <li
                        key={index}
                        className={
                          currentSpeed === item.value ? 'colorBlue' : ''
                        }
                        onClick={() => {
                          setCurrentSpeed(item.value);
                          setSpeedListShow(false);
                          if (playerRef.current) {
                            playerRef.current.forward(item.value);
                          }
                        }}
                      >
                        {item.label}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        <div
          className="item"
          onClick={() => {
            setActive(true);
          }}
        >
          <img
            className="screenfull"
            src={require('./assets/screenfull.png')}
            alt=""
          />
        </div>
      </div>
      <div className="paddingBox">
        <div className="choose-date">
          <div className="choose">
            <LeftOutlined className="icon" onClick={handleLeftClick} />
            <span className="date">{date}</span>
            <RightOutlined
              className="icon"
              onClick={() => {
                const tomorrow = dayjs(date, 'YYYY-MM-DD').add(1, 'days');
                const today = dayjs().startOf('day');

                if (tomorrow.isAfter(today)) {
                  message.error('不能选择未来日期');
                } else {
                  setDate(tomorrow.format('YYYY-MM-DD'));
                }
              }}
            />
          </div>
          {!startsWithExcludedString(deviceInfo?.deviceType) && (
            <span className="count">当天共计{playbackDates.length}段回放</span>
          )}
        </div>
      </div>

      <div className="scroller" onScroll={(e) => handleScroll(e)}>
        <div className="scroll">
          {!loading ? (
            playbackDates.length > 0 ? (
              playbackDates.map((item, index) => {
                return (
                  <div
                    className={classnames([
                      'item',
                      {
                        active: `${date}-${item}-${index}` === playingDate,
                      },
                    ])}
                    onClick={() => {
                      setPlayingDate(`${date}-${item}-${index}`);
                      setStartTime(item.start);
                      _playback({
                        startTime: item.start,
                      }).then((response) => {
                        setUrl(response.data.hls || response.data.flvs);
                        setTimeout(() => {
                          if (isMute) {
                            playerRef.current.mute();
                          } else {
                            playerRef.current.cancelMute();
                          }
                          if (
                            currentSpeedRef.current !== 1 &&
                            playerRef.current
                          ) {
                            playerRef.current.forward(currentSpeedRef.current);
                          }
                        }, 500);
                        setIsPause(true);
                      });
                    }}
                    key={item.start}
                  >
                    {`${date}-${item}-${index}` === playingDate ? (
                      <img
                        src={require('./assets/status.png')}
                        alt=""
                        className="status"
                      />
                    ) : null}
                    <span className="text">
                      {dayjs
                        .unix(parseInt(item.start / 1000))
                        .format('HH:mm:ss')}
                      ～
                      {dayjs.unix(parseInt(item.end / 1000)).format('HH:mm:ss')}
                    </span>
                  </div>
                );
              })
            ) : (
              <div className="empty">暂无回放视频</div>
            )
          ) : null}
          {hasMore || loading ? <div className="loading">加载中...</div> : null}
        </div>
      </div>
    </div>
  );
};

export default () => {
  return <Playback />;
  // if (navigator.userAgent.indexOf('UCBrowser') > -1) {
  //   return (
  //     <NoticeBar theme="danger">
  //       由于UC不支持播放器播放，请使用其他播放器打开当前地址
  //     </NoticeBar>
  //   );
  // } else {
  //   return <Playback />;
  // }
};
